import PropTypes from 'prop-types';
import { Background } from 'components/Login/Background/Background';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Input, PasswordInputField } from '../../containers/Form';
import { PasswordInput } from '../../components/Inputs/PasswordInput';
import { PasswordRequirementsDisplay } from '../../components/PasswordRequirements/PasswordRequirements';
import { validatePassword } from '../../utils/validation';
import React, { useEffect, useState } from 'react';
import Strings from '../../Strings';
import { Button, BUTTON_TYPE_BLUE, BUTTON_TYPE_WHITE } from '../../components/Buttons/Button';

import { resetPassword } from '../../actions/user-settings';
import { resetPasswordWithLogin } from '../../actions/auth';
import { RESET_PASSWORD_RESULT } from '../../actions/action-types';

function NewPassword(props) {
  const codeFromUrl = new URLSearchParams(window.location.search).get('code');
  
  // Get state passed from login page (for inactive user flow)
  const locationState = props.location?.state || {};
  const loginAfterReset = locationState.loginAfterReset || false;
  const emailHint = locationState.emailHint || '';
  const phoneHint = locationState.phoneHint || '';
  const method = locationState.method || '';
  const reason = locationState.reason || '';

  const [securityCode, setSecurityCode] = useState(codeFromUrl || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [buttonHover, setButtonHover] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [responseMessage, setResponseMessage] = useState('');
  const [touched, setTouched] = useState({});

  const markTouched = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const shouldHighlight = (field) => {
    return touched[field] || buttonHover;
  };

  useEffect(() => {
    const errors = [];

    if (securityCode === '') {
      errors.push({ property: 'securityCode', errors: [Strings.securityCodeIsRequired] });
    }

    if (newPassword === '' || !validatePassword(newPassword).isValid) {
      errors.push({ property: 'newPassword', errors: [Strings.errors.invalidPassword] });
    }

    if (newPassword != confirmPassword) {
      errors.push({ property: 'confirmPassword', errors: [Strings.errors.passwordsDontMatch] });
    }

    setValidationErrors(errors);
  }, [securityCode, newPassword, confirmPassword, codeFromUrl]);

  const onSubmit = () => {
    if (validationErrors.length > 0) {
      return;
    }

    setResponseMessage('');
    
    if (loginAfterReset) {
      // For inactive user flow: reset password and login in one step
      props.onResetPasswordWithLogin(securityCode, newPassword).then(resp => {
        if (resp?.response?.authentication_token) {
          // Auth will be handled by reducer, redirect to home
          props.onAuthenticated();
        } else {
          setResponseMessage(Strings.passwordChangeFailedCheckYourSecurityCode);
          setSecurityCode('');
        }
      });
    } else {
      // Normal flow: reset password and redirect to login
      props.onResetPassword(securityCode, newPassword).then(resp => {
        if (resp.type == RESET_PASSWORD_RESULT) {
          props.toLoginPage();
        } else {
          setResponseMessage(Strings.passwordChangeFailedCheckYourSecurityCodeAndIdentity || 'Password change failed, check your security code and email or mobile.');
          setSecurityCode('');
        }
      });
    }
  };

  const pageTitle = loginAfterReset ? 'Verify Account' : 'Set New Password';
  const contactHint = method === 'email' ? emailHint : phoneHint;
  const methodLabel = method === 'email' ? 'email' : 'phone';

  const getReasonMessage = () => {
    if (reason === 'password_expired') {
      return 'Your password has expired and needs to be updated.';
    }
    return 'Your account has been inactive and requires verification.';
  };

  return (
    <Background title={pageTitle} noLines>
      <div className="recover-password wizard">
        <div className="content-block">
          {loginAfterReset && (
            <p style={{ marginBottom: '16px' }}>
              {getReasonMessage()}
              {' '}A security code has been sent to your {methodLabel}: <strong>{contactHint}</strong>
            </p>
          )}
          <Input
            name="securityCode"
            id="securityCode"
            label={Strings.securityCode}
            type="text"
            value={securityCode || ''}
            onChange={e => setSecurityCode(e.target.value)}
            onBlur={() => markTouched('securityCode')}
            isRequired
            highlightInvalid={shouldHighlight('securityCode')}
            errorsForTooltip={validationErrors.find(v => v.property === 'securityCode')?.errors || []}
          />
          <PasswordInputField
            name="newPassword"
            id="newPassword"
            label={Strings.newPassword}
            value={newPassword || ''}
            onChange={e => setNewPassword(e.target.value)}
            onBlur={() => markTouched('newPassword')}
            isRequired
            highlightInvalid={shouldHighlight('newPassword')}
            errorsForTooltip={validationErrors.find(v => v.property === 'newPassword')?.errors || []}
          />
          <PasswordRequirementsDisplay
            requirements={validatePassword(newPassword).requirements}
            strength={validatePassword(newPassword).strength}
            strengthLabel={validatePassword(newPassword).strengthLabel}
            show={newPassword.length > 0 || touched.newPassword}
          />
          <PasswordInputField
            name="confirmPassword"
            id="confirmPassword"
            label={Strings.confirmPassword}
            value={confirmPassword || ''}
            onChange={e => setConfirmPassword(e.target.value)}
            onBlur={() => markTouched('confirmPassword')}
            isRequired
            highlightInvalid={shouldHighlight('confirmPassword')}
            errorsForTooltip={validationErrors.find(v => v.property === 'confirmPassword')?.errors || []}
          />
          {responseMessage && <p>{responseMessage}</p>}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '20px' }}>
            <Button
              buttonType={BUTTON_TYPE_WHITE}
              type="button"
              onClick={() => props.toLoginPage()}
            >
              {Strings.back}
            </Button>
            <Button
              buttonType={validationErrors.length > 0 ? BUTTON_TYPE_WHITE : BUTTON_TYPE_BLUE}
              type="button"
              disabled={validationErrors.length > 0}
              onClick={() => {
                onSubmit();
              }}
              onMouseEnter={() => setButtonHover(true)}
              onMouseLeave={() => setButtonHover(false)}
            >
              {Strings.submit}
            </Button>
          </div>
        </div>
      </div>
    </Background>
  );
}

NewPassword.propTypes = {
  onResetPassword: PropTypes.func.isRequired,
  onResetPasswordWithLogin: PropTypes.func.isRequired,
  toLoginPage: PropTypes.func.isRequired,
  onAuthenticated: PropTypes.func.isRequired,
  location: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
  onResetPassword: (token, password) => dispatch(resetPassword(token, password)),
  onResetPasswordWithLogin: (token, password) => dispatch(resetPasswordWithLogin(token, password)),
  toLoginPage: () => dispatch(push('/login')),
  onAuthenticated: () => dispatch(push('/')),
});

export default connect(null, mapDispatchToProps)(NewPassword);
